import { useState, useMemo } from 'react'
import { CheckCircle, XCircle, Link2, Volume2 } from 'lucide-react'
import type { Exercise } from '@/types'
import AudioPlayer from '@/components/common/AudioPlayer'
import { decodeMatchingPairs, encodeMatchingPairs, shuffleArray } from '@/lib/exerciseUtils'
import EditableText from '@/components/edit/EditableText'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveField } from '@/lib/editApi'

interface Props {
  exercise: Exercise
  onSetPairs: (pairs: Record<string, string>) => void
  isSubmitted: boolean
  correctDetails: Record<string, boolean>
  skill: string
  onExerciseUpdated?: (updated: Exercise) => void
}

export default function MatchingExercise({ exercise, onSetPairs, isSubmitted, correctDetails, skill, onExerciseUpdated }: Props) {
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()
  const isAudioMode = skill === 'listening'
  const pairs = useMemo(() => decodeMatchingPairs(exercise.options || []), [exercise.options])
  const leftItems = useMemo(() => pairs.map(p => p.left), [pairs])
  const rightItems = useMemo(() => shuffleArray(pairs.map(p => p.right)), [pairs])

  const [selectedLeft, setSelectedLeft] = useState<string | null>(null)
  const [connections, setConnections] = useState<Record<string, string>>({})

  const handleLeftClick = (item: string) => {
    if (isSubmitted) return
    setSelectedLeft(item === selectedLeft ? null : item)
  }

  const handleRightClick = (item: string) => {
    if (isSubmitted || !selectedLeft) return

    const newConnections = { ...connections, [selectedLeft]: item }
    setConnections(newConnections)
    onSetPairs(newConnections)
    setSelectedLeft(null)
  }

  const removeConnection = (leftItem: string) => {
    if (isSubmitted) return
    const newConnections = { ...connections }
    delete newConnections[leftItem]
    setConnections(newConnections)
    onSetPairs(newConnections)
  }

  const isValidUrl = (url: string) => {
    try {
      new URL(url)
      return true
    } catch {
      return false
    }
  }

  const getLeftColor = (item: string) => {
    if (isSubmitted && correctDetails[item] !== undefined) {
      return correctDetails[item] ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
    }
    if (selectedLeft === item) return 'border-blue-500 bg-blue-50'
    if (connections[item]) return 'border-purple-400 bg-purple-50'
    return 'border-gray-200 hover:border-gray-300'
  }

  const getRightColor = (item: string) => {
    const connectedLeft = Object.entries(connections).find(([, v]) => v === item)?.[0]
    if (isSubmitted && connectedLeft && correctDetails[connectedLeft] !== undefined) {
      return correctDetails[connectedLeft] ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
    }
    const isUsed = Object.values(connections).includes(item)
    if (isUsed) return 'border-purple-400 bg-purple-50'
    if (selectedLeft) return 'border-blue-200 hover:border-blue-400 cursor-pointer'
    return 'border-gray-200'
  }

  const getAudioLabel = (index: number) => `Audio ${index + 1}`

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500 text-center mb-2">
        {isAudioMode
          ? 'Escucha cada audio y conectalo con el texto correcto'
          : 'Selecciona un elemento de la izquierda y luego su pareja de la derecha'
        }
      </p>

      <div className="grid grid-cols-[1fr_40px_1fr] gap-2">
        {/* Columna izquierda */}
        <div className="space-y-2">
          <div className={`text-xs font-semibold text-center py-1 rounded ${
            isAudioMode
              ? 'text-blue-600 bg-blue-50'
              : 'text-purple-600 bg-purple-50'
          }`}>
            {isAudioMode ? (
              <span className="flex items-center justify-center gap-1">
                <Volume2 className="w-3 h-3" />
                Audio
              </span>
            ) : 'Columna A'}
          </div>
          {leftItems.map((item, i) => (
            <button
              key={i}
              onClick={() => connections[item] ? removeConnection(item) : handleLeftClick(item)}
              disabled={isSubmitted}
              className={`w-full p-3 rounded-xl border-2 text-left transition-all text-sm font-medium ${getLeftColor(item)}`}
            >
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  {isAudioMode && <Volume2 className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                  <span className="flex-1">
                    {isAudioMode ? getAudioLabel(i) : item}
                  </span>
                  {connections[item] && !isSubmitted && (
                    <Link2 className="w-4 h-4 text-purple-400" />
                  )}
                  {isSubmitted && correctDetails[item] !== undefined && (
                    correctDetails[item]
                      ? <CheckCircle className="w-4 h-4 text-green-500" />
                      : <XCircle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                {/* Reproductor de audio inline */}
                {isAudioMode && isValidUrl(item) && (
                  <span onClick={(e) => e.stopPropagation()}>
                    <AudioPlayer src={item} compact className="w-full" />
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* Lineas de conexion */}
        <div className="flex flex-col justify-center items-center gap-2 py-7">
          {leftItems.map((_, i) => (
            <div key={i} className={`${isAudioMode ? 'h-[80px]' : 'h-[52px]'} flex items-center`}>
              <div className={`w-4 border-t-2 ${
                isSubmitted ? 'border-gray-300' : 'border-gray-200'
              }`} />
            </div>
          ))}
        </div>

        {/* Columna derecha */}
        <div className="space-y-2">
          <div className="text-xs font-semibold text-indigo-600 text-center py-1 bg-indigo-50 rounded">
            {isAudioMode ? 'Texto' : 'Columna B'}
          </div>
          {rightItems.map((item, i) => {
            const isUsed = Object.values(connections).includes(item)
            return (
              <button
                key={i}
                onClick={() => handleRightClick(item)}
                disabled={isSubmitted || (!selectedLeft && !isUsed)}
                className={`w-full ${isAudioMode ? 'min-h-[80px]' : ''} p-3 rounded-xl border-2 text-left transition-all text-sm font-medium ${getRightColor(item)} ${
                  !selectedLeft && !isSubmitted ? 'opacity-75' : ''
                }`}
              >
                {item}
              </button>
            )
          })}
        </div>
      </div>

      {/* Conexiones actuales */}
      {Object.keys(connections).length > 0 && !isSubmitted && (
        <div className="p-3 bg-purple-50 rounded-lg">
          <p className="text-xs font-medium text-purple-600 mb-2">Parejas conectadas:</p>
          <div className="space-y-1">
            {Object.entries(connections).map(([left, right]) => (
              <p key={left} className="text-sm text-purple-800">
                {isAudioMode ? getAudioLabel(leftItems.indexOf(left)) : left} → {right}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Mostrar respuestas correctas despues de submit */}
      {isSubmitted && Object.values(correctDetails).some(v => !v) && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-xs font-medium text-green-600 mb-2">Parejas correctas:</p>
          <div className="space-y-1">
            {pairs.map((pair, i) => (
              <p key={i} className="text-sm text-green-800">
                {isAudioMode ? getAudioLabel(i) : pair.left} → {pair.right}
              </p>
            ))}
          </div>
        </div>
      )}

      {isEditMode && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
          <p className="text-xs font-medium text-amber-700 mb-1">
            Pares correctos ({isAudioMode ? 'columna A es la URL del audio' : 'edita ambos lados'}):
          </p>
          <div className="space-y-2">
            {pairs.map((pair, i) => {
              const savePairSide = (side: 'left' | 'right') => async (newText: string) => {
                if (!profile?.id) return { error: new Error('No autenticado') }
                const newPairs = pairs.map((p, idx) => idx === i ? { ...p, [side]: newText } : p)
                const newOptions = encodeMatchingPairs(newPairs)
                const { error } = await saveField({
                  table: 'exercises', id: exercise.id, field: 'options',
                  oldValue: JSON.stringify(exercise.options || []),
                  newValue: JSON.stringify(newOptions),
                  dbValue: newOptions,
                  editedBy: profile.id
                })
                if (!error) onExerciseUpdated?.({ ...exercise, options: newOptions })
                return { error }
              }
              return (
                <div key={i} className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center text-sm">
                  <EditableText
                    as="div"
                    value={pair.left}
                    customSave={savePairSide('left')}
                    placeholder="(vacío)"
                    className="text-amber-900 px-2 py-1 bg-white rounded border border-amber-200"
                  />
                  <span className="text-amber-600">→</span>
                  <EditableText
                    as="div"
                    value={pair.right}
                    customSave={savePairSide('right')}
                    placeholder="(vacío)"
                    className="text-amber-900 px-2 py-1 bg-white rounded border border-amber-200"
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
